import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLocale } from '../../contexts/LocaleContext';
import { API_BASE } from '../../config';
import { MarkAsDoneModal } from '../MarkAsDoneModal';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { AdminAccessDenied } from './AdminAccessDenied';
import {
  ADMIN_BADGE_SUCCESS_CLASSNAME,
  ADMIN_BADGE_WARNING_CLASSNAME,
  ADMIN_FILTER_BAR_CLASSNAME,
  ADMIN_PAGE_NARROW_CLASSNAME,
  ADMIN_TABLE_CELL_MUTED_CLASSNAME,
  ADMIN_TABLE_LINK_CLASSNAME,
  AdminEmptyState,
  AdminErrorAlert,
  AdminLoadingState,
  AdminTableWrap,
} from './admin-ui';

interface UserFeedback {
  id: string;
  userId: string | null;
  userEmail: string | null;
  message: string;
  githubIssueUrl: string | null;
  done: boolean;
  createdAt: string;
}

export const UserFeedback: React.FC = () => {
  const { user, token } = useAuth();
  const { t } = useLocale();
  const [feedback, setFeedback] = useState<UserFeedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingUrl, setEditingUrl] = useState<string>('');
  const [showUntrackedOnly, setShowUntrackedOnly] = useState<boolean>(true);
  const [showDone, setShowDone] = useState<boolean>(false);
  const [markAsDoneId, setMarkAsDoneId] = useState<string | null>(null);
  const [isMarkingAsDone, setIsMarkingAsDone] = useState(false);

  useEffect(() => {
    if (!user || !user.admin) {
      return;
    }

    const fetchFeedback = async (): Promise<void> => {
      try {
        const response = await fetch(`${API_BASE}/api/user-feedback`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch feedback');
        }

        const data = await response.json();
        setFeedback(data.feedback || []);
      } catch (error) {
        console.error('Error fetching feedback:', error);
        setError(t('failed_to_load_feedback'));
      } finally {
        setLoading(false);
      }
    };

    fetchFeedback();
  }, [user, token]);

  const handleStartEdit = (item: UserFeedback): void => {
    setEditingId(item.id);
    setEditingUrl(item.githubIssueUrl || '');
  };

  const handleCancelEdit = (): void => {
    setEditingId(null);
    setEditingUrl('');
  };

  const formatGithubIssueUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/').filter(Boolean);
      if (pathParts.length >= 3 && pathParts[2] === 'issues' && pathParts[3]) {
        const repo = pathParts[1];
        const issueId = pathParts[3];
        return `${repo}#${issueId}`;
      }
      return url;
    } catch {
      return url;
    }
  };

  const handleSaveGithubIssueUrl = async (feedbackId: string): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE}/api/user-feedback/${feedbackId}/github-issue-url`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ githubIssueUrl: editingUrl.trim() || null }),
      });

      if (!response.ok) {
        throw new Error('Failed to update GitHub issue URL');
      }

      const data = await response.json();
      setFeedback((prev) => prev.map((item) => (item.id === feedbackId ? data.feedback : item)));
      setEditingId(null);
      setEditingUrl('');
      alert(t('github_issue_url_updated'));
    } catch (error) {
      console.error('Error updating GitHub issue URL:', error);
      alert(t('failed_to_update_github_issue'));
    }
  };

  const handleMarkAsDone = async (feedbackId: string, message: string | null): Promise<void> => {
    setIsMarkingAsDone(true);
    try {
      const response = await fetch(`${API_BASE}/api/user-feedback/${feedbackId}/done`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to mark feedback as done');
      }

      const data = await response.json();
      setFeedback((prev) => prev.map((item) => (item.id === feedbackId ? data.feedback : item)));
      setMarkAsDoneId(null);
    } catch (error) {
      console.error('Error marking feedback as done:', error);
      alert(error instanceof Error ? error.message : t('failed_to_mark_feedback_as_done'));
    } finally {
      setIsMarkingAsDone(false);
    }
  };

  if (!user || !user.admin) {
    return <AdminAccessDenied />;
  }

  return (
    <>
      <div className={ADMIN_PAGE_NARROW_CLASSNAME}>
        <div className={ADMIN_FILTER_BAR_CLASSNAME}>
          <Button variant="outline" size="sm" asChild>
            <a
              href="https://github.com/users/juanignaciosl/projects/2/views/1"
              target="_blank"
              rel="noopener noreferrer"
            >
              Project
            </a>
          </Button>
          <Button size="sm" asChild>
            <a
              href="https://github.com/juanignaciosl/narrativebench/issues/new"
              target="_blank"
              rel="noopener noreferrer"
            >
              New Issue
            </a>
          </Button>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <Label className="flex cursor-pointer items-center gap-2 text-sm text-foreground">
            <Checkbox
              checked={showUntrackedOnly}
              onCheckedChange={(checked) => setShowUntrackedOnly(checked === true)}
            />
            <span>Untracked</span>
          </Label>
          <Label className="flex cursor-pointer items-center gap-2 text-sm text-foreground">
            <Checkbox
              checked={showDone}
              onCheckedChange={(checked) => setShowDone(checked === true)}
            />
            <span>Done</span>
          </Label>
        </div>

        {loading ? (
          <AdminLoadingState />
        ) : error ? (
          <AdminErrorAlert>{error}</AdminErrorAlert>
        ) : (() => {
          let filteredFeedback = feedback;

          if (showUntrackedOnly) {
            filteredFeedback = filteredFeedback.filter((item) => !item.githubIssueUrl);
          }

          if (!showDone) {
            filteredFeedback = filteredFeedback.filter((item) => !item.done);
          }

          return filteredFeedback.length === 0 ? (
            <AdminEmptyState>
              {showUntrackedOnly && feedback.length > 0
                ? 'No untracked feedback entries'
                : t('no_feedback_submitted')}
            </AdminEmptyState>
          ) : (
            <AdminTableWrap>
              <table className="admin-data-table min-w-[900px]">
                <thead>
                  <tr>
                    <th>
                      {t('date')}
                    </th>
                    <th>
                      {t('user')}
                    </th>
                    <th>
                      {t('feedback_message')}
                    </th>
                    <th>
                      {t('github_issue')}
                    </th>
                    <th>
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFeedback.map((item) => (
                  <tr key={item.id}>
                    <td className={`${ADMIN_TABLE_CELL_MUTED_CLASSNAME} whitespace-nowrap`}>
                      {new Date(item.createdAt).toLocaleString()}
                    </td>
                    <td>
                      {item.userEmail || t('no')}
                    </td>
                    <td className="max-w-[600px]">
                      <div className="whitespace-pre-wrap break-words">
                        {item.message}
                      </div>
                    </td>
                    <td>
                      {editingId === item.id ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <Input
                            type="url"
                            value={editingUrl}
                            onChange={(e) => setEditingUrl(e.target.value)}
                            placeholder={t('github_issue_url_placeholder')}
                            className="min-w-[250px]"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleSaveGithubIssueUrl(item.id);
                              } else if (e.key === 'Escape') {
                                handleCancelEdit();
                              }
                            }}
                          />
                          <Button size="sm" onClick={() => handleSaveGithubIssueUrl(item.id)}>
                            {t('save')}
                          </Button>
                          <Button size="sm" variant="outline" onClick={handleCancelEdit}>
                            {t('cancel')}
                          </Button>
                        </div>
                      ) : (
                        <div className="flex flex-wrap items-center gap-2">
                          {item.githubIssueUrl ? (
                            <>
                              <a
                                href={item.githubIssueUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={ADMIN_TABLE_LINK_CLASSNAME}
                              >
                                {formatGithubIssueUrl(item.githubIssueUrl)}
                              </a>
                              <Button size="sm" variant="outline" onClick={() => handleStartEdit(item)}>
                                {t('edit')}
                              </Button>
                            </>
                          ) : (
                            <Button size="sm" variant="outline" onClick={() => handleStartEdit(item)}>
                              {t('link_github_issue')}
                            </Button>
                          )}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="flex flex-wrap items-center gap-2">
                        {item.done ? (
                          <span className={ADMIN_BADGE_SUCCESS_CLASSNAME}>
                            Done
                          </span>
                        ) : (
                          <>
                            <span className={ADMIN_BADGE_WARNING_CLASSNAME}>
                              Pending
                            </span>
                            <Button size="sm" onClick={() => setMarkAsDoneId(item.id)}>
                              {t('mark_as_done')}
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  ))}
                </tbody>
              </table>
            </AdminTableWrap>
          );
        })()}
      </div>
      <MarkAsDoneModal
        isOpen={markAsDoneId !== null}
        onClose={() => setMarkAsDoneId(null)}
        onSubmit={(message) => {
          if (markAsDoneId) {
            handleMarkAsDone(markAsDoneId, message);
          }
        }}
        isLoading={isMarkingAsDone}
      />
    </>
  );
};
